import { TICK_RATE, Team, otherTeam } from "../constants.ts";
import { pointAt } from "../map/mapdata.ts";
import { GameMode } from "../map/maps.ts";
import { emptyStats, type Sim } from "../sim/sim.ts";

export enum Phase {
  /** Free play while players gather; instant respawns, point inactive. */
  Warmup = 0,
  /** Round is about to start: point locked. */
  Setup = 1,
  InProgress = 2,
  RoundEnd = 3,
  MatchEnd = 4,
}

export interface MatchSettings {
  /** Control: seconds before the point unlocks (Rivals' Domination: 30). */
  setupSeconds: number;
  /** Convoy: seconds the defenders get before the attackers' gates open (Rivals: 40). */
  convoySetupSeconds: number;
  /** Seconds for a single team to capture a neutral/enemy point. */
  captureSeconds: number;
  /** Seconds of ownership per 1% of round progress. */
  secondsPerPercent: number;
  roundsToWin: number;
  roundEndSeconds: number;
  matchEndSeconds: number;
  respawnSeconds: number;
  /** Convoy: multiplies the map's attack time (starting clock and checkpoint bonuses). */
  convoyTime: number;
}

export const DEFAULT_MATCH_SETTINGS: MatchSettings = {
  setupSeconds: 30,
  convoySetupSeconds: 40,
  captureSeconds: 10,
  // Rivals' Domination: 1% of the round every ~1.2 s the point is held.
  secondsPerPercent: 1.2,
  roundsToWin: 2,
  roundEndSeconds: 6,
  matchEndSeconds: 12,
  respawnSeconds: 10,
  convoyTime: 1,
};

/** What the payload is doing this tick. */
export enum CartState {
  Idle = 0,
  /** Attackers alone beside it: it moves. */
  Pushing = 1,
  /** Attackers and defenders beside it: it holds. */
  Contested = 2,
  /** Only defenders beside it: it holds. */
  Blocked = 3,
  /** No attackers for a while: it creeps back towards the last checkpoint. */
  Rolling = 4,
}

export interface MatchState {
  mode: GameMode;
  phase: Phase;
  phaseTicks: number; // ticks remaining in timed phases
  round: number;
  /** Control: rounds won. Convoy: checkpoints passed, plus one for delivering the payload. */
  score: [number, number];
  /** Control: round percentage. Convoy: how far along the track each team has pushed, 0..100. */
  progress: [number, number];
  owner: Team;
  /** Capture progress 0..1 by `capTeam` toward taking the point. */
  capture: number;
  capTeam: Team;
  contested: boolean;
  overtime: boolean;
  /** Players on the point (Control) or beside the payload (Convoy), per team. */
  onPoint: [number, number];
  /** Round winner at RoundEnd, match winner at MatchEnd; None is a draw. */
  winner: Team;
  /** Control: the stage (sub-map) in play. Each round of Domination is played on another one. */
  stage: number;
  // ---- Convoy
  attacker: Team;
  /** Payload distance along the track in whole millimetres (replicated exactly). */
  cartMm: number;
  /** Checkpoints passed this round. */
  checkpoint: number;
  /** Attack ticks left; 0 means overtime or the end. */
  clock: number;
  cartState: CartState;
  /** Round two: whether the first attack delivered, and with how many ticks left. */
  goalDone: boolean;
  goalTicks: number;
}

export const newMatchState = (mode: GameMode = GameMode.Control): MatchState => ({
  mode,
  phase: Phase.Warmup,
  phaseTicks: 0,
  round: 0,
  score: [0, 0],
  progress: [0, 0],
  owner: Team.None,
  capture: 0,
  capTeam: Team.None,
  contested: false,
  overtime: false,
  onPoint: [0, 0],
  winner: Team.None,
  stage: 0,
  attacker: Team.A,
  cartMm: 0,
  checkpoint: 0,
  clock: 0,
  cartState: CartState.Idle,
  goalDone: false,
  goalTicks: 0,
});

export const ti = (t: Team) => (t === Team.A ? 0 : 1);

/**
 * The round/phase machine every mode shares: setup, play, round end, match end, back to warmup.
 * Modes supply the objective. Server-only; state is replicated in snapshots.
 */
export abstract class MatchRules {
  state: MatchState;
  onPhaseChange?: (phase: Phase, prev: Phase) => void;

  constructor(
    readonly sim: Sim,
    public settings: MatchSettings,
    readonly mode: GameMode,
  ) {
    sim.canRespawn = () => true;
    this.state = newMatchState(mode);
  }

  get respawnTicks() {
    return this.state.phase === Phase.Warmup ? 2 * TICK_RATE : this.settings.respawnSeconds * TICK_RATE;
  }

  startMatch() {
    this.state = newMatchState(this.mode);
    // Warmup is practice: the match starts everyone with no stats and no ult charge. Ult then
    // carries from round to round, as in Rivals' Domination.
    for (const eid of this.sim.playerEids()) {
      this.sim.c.Ult.charge[eid] = 0;
      const m = this.sim.players.get(eid);
      if (m) m.stats = emptyStats();
    }
    this.startRound();
  }

  toWarmup() {
    const prev = this.state.phase;
    // Warmup goes on where the match ended: everyone is still standing in that stage.
    const stage = this.state.stage;
    this.state = newMatchState(this.mode);
    this.state.stage = stage;
    this.enterWarmup();
    this.onPhaseChange?.(Phase.Warmup, prev);
  }

  protected setPhase(p: Phase, seconds: number) {
    const prev = this.state.phase;
    this.state.phase = p;
    this.state.phaseTicks = Math.round(seconds * TICK_RATE);
    this.onPhaseChange?.(p, prev);
  }

  /** Call once per server tick after sim.stepWorld(). */
  tick() {
    const s = this.state;
    const sim = this.sim;
    const c = sim.c;
    // Adjust respawn timers for newly-dead players.
    for (const eid of sim.playerEids()) {
      if (!c.Player.alive[eid] && c.Player.respawn[eid]! > this.respawnTicks) c.Player.respawn[eid] = this.respawnTicks;
    }
    s.onPoint = this.countObjective();
    // Rivals: heroes gain ult energy every second once the round has started.
    sim.ultPassive = s.phase === Phase.InProgress;

    if (s.phase === Phase.Warmup) return;
    if (s.phaseTicks > 0) s.phaseTicks--;

    switch (s.phase) {
      case Phase.Setup:
        if (s.phaseTicks <= 0) this.unlock();
        break;
      case Phase.InProgress:
        this.tickObjective(s.onPoint);
        break;
      case Phase.RoundEnd:
        if (s.phaseTicks <= 0) {
          if (this.matchOver()) this.setPhase(Phase.MatchEnd, this.settings.matchEndSeconds);
          else this.startRound();
        }
        break;
      case Phase.MatchEnd:
        if (s.phaseTicks <= 0) this.toWarmup();
        break;
    }
  }

  /** Players of each team inside the objective, crediting objective time while in progress. */
  protected countIn(inside: (x: number, y: number, z: number) => boolean): [number, number] {
    const sim = this.sim;
    const c = sim.c;
    const on: [number, number] = [0, 0];
    for (const eid of sim.playerEids()) {
      if (!c.Player.alive[eid] || !inside(c.Pos.x[eid]!, c.Pos.y[eid]!, c.Pos.z[eid]!)) continue;
      const team = c.Player.team[eid] as Team;
      if (team !== Team.A && team !== Team.B) continue;
      on[ti(team)]++;
      if (this.state.phase === Phase.InProgress) {
        const m = sim.players.get(eid);
        if (m) m.stats.objectiveTicks++;
      }
    }
    return on;
  }

  protected unlock() {
    this.setPhase(Phase.InProgress, 0);
  }

  protected enterWarmup() {}

  protected abstract startRound(): void;
  protected abstract countObjective(): [number, number];
  protected abstract tickObjective(on: [number, number]): void;
  /** At the end of RoundEnd: is the match decided? (Sets state.winner if so.) */
  protected abstract matchOver(): boolean;
}

/**
 * Control (King of the Hill) rules, as Rivals' Domination: a map may be built in stages (sub-maps,
 * one POINT_* each), and every round is played on another stage, in an order rolled per match.
 */
export class ControlMatch extends MatchRules {
  /** The stages this match plays, one per round (repeating if it runs longer). */
  order: number[] = [0];
  /** A fixed order for the next match instead of a rolled one (tests, tools). */
  plan?: number[];
  /** The stage the next match starts on (the host's pick); the rest stay rolled. -1: rolled too. */
  first = -1;

  constructor(
    sim: Sim,
    settings: MatchSettings = DEFAULT_MATCH_SETTINGS,
    private readonly rng: () => number = Math.random,
  ) {
    super(sim, settings, GameMode.Control);
    this.sync();
  }

  /** How many stages the map has. */
  get stages() {
    return Math.max(1, this.sim.map.points.length);
  }

  startMatch() {
    const order = Array.from({ length: this.stages }, (_, i) => i);
    for (let i = order.length - 1; i > 0; i--) {
      const j = Math.floor(this.rng() * (i + 1));
      [order[i], order[j]] = [order[j]!, order[i]!];
    }
    this.order = this.plan?.filter((k) => k >= 0 && k < this.stages) ?? order;
    if (!this.order.length) this.order = order;
    if (!this.plan && this.first >= 0 && this.first < this.stages) this.order = [this.first, ...this.order.filter((k) => k !== this.first)];
    super.startMatch();
  }

  /** Warmup: moves everyone to another stage (host or cheats; see the room's /stage). */
  setStage(stage: number) {
    if (this.state.phase !== Phase.Warmup || stage < 0 || stage >= this.stages) return false;
    this.state.stage = stage;
    this.sync();
    for (const eid of this.sim.playerEids()) this.sim.respawn(eid);
    for (const hp of this.sim.healthPacks) hp.cooldown = 0;
    return true;
  }

  /** Both sides spawn in the stage in play. */
  private sync() {
    this.sim.spawnSide = (t) => t;
    this.sim.spawnStage = [this.state.stage, this.state.stage];
  }

  protected startRound() {
    const s = this.state;
    s.round++;
    s.stage = this.order[(s.round - 1) % this.order.length] ?? 0;
    this.sync();
    s.progress = [0, 0];
    s.owner = Team.None;
    s.capture = 0;
    s.capTeam = Team.None;
    s.overtime = false;
    s.contested = false;
    // Everyone back to spawn, fresh.
    for (const eid of this.sim.playerEids()) this.sim.respawn(eid);
    for (const hp of this.sim.healthPacks) hp.cooldown = 0;
    this.setPhase(Phase.Setup, this.settings.setupSeconds);
  }

  protected countObjective() {
    const pt = pointAt(this.sim.map, this.state.stage);
    return this.countIn((x, y, z) => {
      const dx = x - pt.pos.x;
      const dz = z - pt.pos.z;
      const dy = y - pt.pos.y;
      return dx * dx + dz * dz <= pt.radius * pt.radius && dy > -1 && dy < 4;
    });
  }

  protected matchOver() {
    const s = this.state;
    if (s.score[0] < this.settings.roundsToWin && s.score[1] < this.settings.roundsToWin) return false;
    s.winner = s.score[0] > s.score[1] ? Team.A : Team.B;
    return true;
  }

  protected tickObjective(on: [number, number]) {
    const s = this.state;
    const aOn = on[0] > 0;
    const bOn = on[1] > 0;
    s.contested = aOn && bOn;
    const capRate = 1 / (this.settings.captureSeconds * TICK_RATE);

    if (!s.contested) {
      const present = aOn ? Team.A : bOn ? Team.B : Team.None;
      if (present !== Team.None && present !== s.owner) {
        if (s.capTeam !== present && s.capture > 0) s.capture = Math.max(0, s.capture - capRate);
        else {
          s.capTeam = present;
          s.capture += capRate;
          if (s.capture >= 1) {
            s.owner = present;
            s.capture = 0;
            s.capTeam = Team.None;
          }
        }
      } else if (present === Team.None || present === s.owner) {
        // Uncontested decay of any partial capture.
        s.capture = Math.max(0, s.capture - capRate * 0.5);
        if (s.capture === 0) s.capTeam = Team.None;
      }
    }

    if (s.owner !== Team.None) {
      const oi = ti(s.owner);
      const threatened = s.contested || s.capture > 0 || on[ti(otherTeam(s.owner))] > 0;
      const rate = 1 / (this.settings.secondsPerPercent * TICK_RATE);
      const next = s.progress[oi] + (s.contested ? 0 : rate);
      if (next >= 99 && threatened) {
        s.progress[oi] = Math.max(s.progress[oi], 99);
        s.overtime = true;
      } else {
        s.overtime = false;
        s.progress[oi] = Math.min(100, next);
      }
      if (s.progress[oi] >= 100) {
        s.score[oi]++;
        s.winner = s.owner;
        this.setPhase(Phase.RoundEnd, this.settings.roundEndSeconds);
      }
    }
  }
}
